import { DownloadOutlined, ExportOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Modal, Spin } from 'antd';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage, useFileUrl } from '../api/client';
import { neutral } from '../theme';

interface ViewerState {
  title: string;
  fileName: string;
  url?: string;
  type?: string;
  error?: string;
}

interface FileViewer {
  /** Shows a protected PDF or image in the viewer; `load` returns its API path. */
  show: (title: string, load: () => Promise<string>, fileName?: string) => void;
}

const Ctx = createContext<FileViewer | undefined>(undefined);

/**
 * In-app viewer of generated documents and attachments: the file is fetched with the bearer
 * token and shown in a modal, with download and new-tab links the user clicks (no pop-up).
 */
export function FileViewerProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const fileUrl = useFileUrl();
  const [state, setState] = useState<ViewerState>();

  const show = useCallback(
    (title: string, load: () => Promise<string>, fileName = `${title}.pdf`) => {
      setState({ title, fileName });
      load()
        .then((path) => fileUrl(path))
        .then(async (url) => {
          const type = (await fetch(url).then((r) => r.blob())).type;
          setState((s) => (s ? { ...s, url, type } : s));
        })
        .catch((e) => setState((s) => (s ? { ...s, error: errorMessage(e) } : s)));
    },
    [fileUrl],
  );

  const close = () => {
    if (state?.url) URL.revokeObjectURL(state.url);
    setState(undefined);
  };
  const value = useMemo(() => ({ show }), [show]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <Modal
        open={Boolean(state)}
        onCancel={close}
        title={state?.title}
        width={960}
        centered
        styles={{ body: { height: '76vh', padding: 0 } }}
        footer={
          state?.url && (
            <Flex justify="flex-end" gap={8}>
              <Button href={state.url} target="_blank" rel="noopener" icon={<ExportOutlined />}>
                {t('viewer.openTab')}
              </Button>
              <Button
                type="primary"
                href={state.url}
                download={state.fileName}
                icon={<DownloadOutlined />}
              >
                {t('viewer.download')}
              </Button>
            </Flex>
          )
        }
        destroyOnHidden
      >
        {state?.error ? (
          <Alert type="error" showIcon title={state.error} style={{ margin: 16 }} />
        ) : !state?.url ? (
          <Flex align="center" justify="center" style={{ height: '100%' }} vertical gap={12}>
            <Spin size="large" />
            <span style={{ color: neutral.foreground3 }}>{t('viewer.preparing')}</span>
          </Flex>
        ) : state.type?.startsWith('image/') ? (
          <Flex
            align="center"
            justify="center"
            style={{ height: '100%', background: neutral.background3 }}
          >
            <img
              src={state.url}
              alt={state.title}
              style={{ maxWidth: '100%', maxHeight: '100%' }}
            />
          </Flex>
        ) : (
          <iframe
            title={state.title}
            src={state.url}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
        )}
      </Modal>
    </Ctx.Provider>
  );
}

export function useFileViewer(): FileViewer {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useFileViewer must be used inside FileViewerProvider');
  return ctx;
}
